import LocalEventsManager from 'core/localEventsManager';
import { isEqual } from 'lodash';
import { Utils } from 'tools';
import { apiFetch } from 'framework/api';
import { itemToForm, preparedProperties } from './formHelpers';
import { copyQualifier, loadQualifierTemplate, QualifierRootData } from './api';
import qualifierCache from 'areas/services/designer/qualifierCache';
import { QualifierWizardStore } from './qualifierWizardStore';
import { AssetTarget } from './components/multiple-qualifiers-window/types';
import { FormBuilderValue } from 'controls/react/form/formBuilderNew/models';

// mxGraph cell — untyped in this codebase, kept loose like the rest of the designer integration
export type DesignerCell = {
	customData: { id: string; qualifiers: QualifierRootData[]; sqData?: Record<string, unknown> };
};

const qualifierCacheTyped = qualifierCache as { cache: Record<string, QualifierRootData> };

export class QualifierSaver {
	private templates: QualifierRootData[] = [];
	private assetTargets: AssetTarget[] = [];

	constructor(
		private store: QualifierWizardStore,
		private cell: DesignerCell,
	) {}

	private qualifierInfo(): Record<string, { value: unknown; text: string }> {
		const info: Record<string, { value: unknown; text: string }> = {};
		this.store.formStore.allProperties.forEach((property) => {
			info[property.id] = { value: this.store.formStore.value[property.id], text: property.text };
		});
		return info;
	}

	private mergeInfo(info: Record<string, { value: unknown; text: string }>, targets: AssetTarget) {
		return {
			...info,
			assetId: { text: targets.assetName, value: targets.assetId },
			targetId: { text: targets.targetName, value: targets.targetId },
		};
	}

	private updateProperties(
		properties: { id: string; properties?: unknown[]; value?: unknown; text?: string }[],
		finalInfo: Record<string, { value: unknown; text: string }>,
	) {
		for (const property of properties) {
			if (property.properties) {
				this.updateProperties(property.properties as typeof properties, finalInfo);
			} else {
				const infoItem = finalInfo[property.id];
				property.value = infoItem.value;
				property.text = infoItem.text;
			}
		}
	}

	// builds a duplicate qualifier for one selected asset target — same values as the qualifier being edited,
	// except assetId/targetId, and the name/configurationName suffixed with the asset's name.
	private buildDuplicateConfig(template: QualifierRootData, item: AssetTarget): QualifierRootData {
		const { warning, ...values } = this.store.formStore.value as FormBuilderValue & { warning?: unknown };
		const name = `${template.name} - ${item.assetName}`;

		this.updateProperties(template.configuration.properties as never, this.mergeInfo(this.qualifierInfo(), item));

		return {
			...template,
			name,
			configuration: {
				...this.store.original.configuration,
				configurationName: `${this.store.original.configuration.configurationName} - ${item.assetName}`,
				values: {
					...values,
					assetId: item.assetId,
					targetId: item.targetId,
					name,
				},
				warning: warning,
			},
		};
	}

	async save() {
		const { serviceId, templateId, qualifierId } = this.store;

		for (const item of this.store.assetTargets) {
			this.assetTargets.push(item);

			const result = this.store.isCreateMode
				? await apiFetch(loadQualifierTemplate(serviceId, templateId))
				: await apiFetch(copyQualifier(serviceId, qualifierId));

			this.templates.push(this.buildDuplicateConfig(result.data, item));
		}

		this.performSaving();
	}

	private triggerStoreEvent(accountId: string, agentId: string, assetId: string, assetAccountId: string) {
		LocalEventsManager.trigger('store_qualifier_settings', {
			settings: { accountId, agentId, assetId, assetAccountId },
		});
	}

	private storeQualifierSettings() {
		const value = this.store.formStore.value;
		this.triggerStoreEvent(
			value.accountId as string,
			value.agentId as string,
			value.assetId as string,
			value.assetAccountId as string,
		);

		for (const template of this.templates) {
			const values = template.configuration.values;
			this.triggerStoreEvent(
				values.accountId as string,
				values.agentId as string,
				values.assetId as string,
				values.assetAccountId as string,
			);
		}
	}

	private getConfigFromCurrent(): QualifierRootData {
		const { warning, ...values } = this.store.formStore.value as FormBuilderValue & { warning?: unknown };

		return {
			id: this.store.qualifierId,
			className: this.store.original.className,
			name: values.name as string,
			description: values.description as string,
			agentId: values.agentId as string,
			configuration: {
				...this.store.original.configuration,
				values,
				warning: warning,
			},
			shared: this.store.original.shared,
			state: 'ACTIVE',
			templateId: this.store.templateId,
		};
	}

	private performSaving() {
		this.storeQualifierSettings();

		const properties = this.store.original.configuration.properties;

		this.updateQualifier(this.getConfigFromCurrent(), properties, this.qualifierInfo());

		this.assetTargets.forEach((targets, index) => {
			const template = this.templates[index];
			this.updateQualifier(
				template,
				template.configuration.properties,
				this.mergeInfo(this.qualifierInfo(), targets),
			);
		});
	}

	private updateQualifier(
		config: QualifierRootData,
		properties: unknown[],
		info: Record<string, { value: unknown; text: string }>,
	) {
		const { qualifiers } = this.cell.customData;

		const index = qualifiers.findIndex((x) => x.id === config.id);
		if (index >= 0) {
			qualifiers[index] = config;
		} else {
			qualifiers.push(config);
		}

		qualifierCacheTyped.cache[config.id] = config;

		this.prepareDataForDetails(config, properties, info);

		$(document).trigger('qualifier_added.sd', [this.cell, config]);
	}

	private convertPropertiesToFields(
		properties: { id: string; properties?: unknown[] }[],
		out: Record<string, unknown> = {},
	) {
		for (const property of properties) {
			if (property.properties) {
				this.convertPropertiesToFields(property.properties as typeof properties, out);
			} else {
				out[property.id] = property;
			}
		}
		return out;
	}

	private prepareDataForDetails(
		config: QualifierRootData,
		properties: unknown[],
		info: Record<string, { value: unknown; text: string }>,
	) {
		if (!this.cell.customData.sqData) {
			this.cell.customData.sqData = {};
		}

		const { sqData } = this.cell.customData;
		const { id, className } = config;

		const data: Record<string, unknown> = { id, className, dirty: true };
		sqData[id] = data;

		const fields = this.convertPropertiesToFields(properties as { id: string; properties?: unknown[] }[]);
		data.fields = fields;

		const { warning, values } = config.configuration;
		const details: Record<string, unknown> = { warning };
		const templateValues: Record<string, unknown> = {};

		data.details = details;
		data.templateValues = templateValues;

		for (const field of Object.keys(fields)) {
			if (info[field] === undefined) continue;

			if (values[field] !== undefined) {
				if (isEqual(info[field].value, values[field])) {
					details[field] = { ...info[field] };
					templateValues[field] = info[field].value;
				} else {
					details[field] = { value: values[field], text: values[field] };
					templateValues[field] = values[field];
				}
			} else {
				details[field] = { ...info[field] };
				templateValues[field] = info[field].value;
			}
		}

		$(document).trigger(`qualifier_info_updated::${id}`, data);
	}
}

export async function loadQualifierDetails(url: string) {
	// @ts-expect-error tools/utils.js's ajaxPromise has default params for timeout/customTimeout, but its inferred
	// type doesn't mark them optional — pre-existing gap, not related to this port
	const { result } = await Utils.ajaxPromise(url, 'GET', {});
	const staticProperties = {
		configurationName: result.data.configuration.configurationName,
		configurationDescription: result.data.configuration.configurationDescription,
	};
	const { properties, forms: originalForms, warning, values } = result.data.configuration;
	const { plain: fields, info: details } = preparedProperties(properties, itemToForm(originalForms));

	return {
		id: result.data.id,
		className: result.data.className,
		externalId: result.data.externalId,
		fields,
		details: {
			warning,
			...details,
		},
		templateValues: values,
		staticProperties,
	};
}
